import moment from 'moment';
import { readDate } from '../../utils/appDate';

/**
 * Who is prompted for the monthly bill, when, and how hard.
 *
 * One field on the business decides all of it. The server sends it inside
 * `business` on the login reply, so the till knows the moment somebody signs
 * in and no extra call is needed:
 *
 *   none    nothing owed. No strip, no dialog, nothing.
 *   loop    owed. Strip and dialog, but the dialog closes.
 *   stuck   overdue. Strip and dialog, and the dialog does not close — the
 *           only two doors are paying and signing out.
 *
 * Nothing here decides whether money is actually due. That is the server's
 * judgement and this file only reads it, which is why a shop cannot clear its
 * own bill by editing anything on this machine.
 */

export const NONE = 'none';
export const LOOP = 'loop';
export const STUCK = 'stuck';

/** Defaults to `none`: a business with no field owes nothing. */
export function notifyOf(business) {
  const value = String(business?.notify || NONE).toLowerCase();
  return value === LOOP || value === STUCK ? value : NONE;
}

export function isDue(business) {
  return notifyOf(business) !== NONE;
}

/** `stuck` — the dialog has no ✕, no Escape and no click-outside. */
export function isLocked(business) {
  return notifyOf(business) === STUCK;
}

/**
 * A waiter is never shown any of this, in any tab, in either state.
 *
 * They are signed in for one order and have no way to settle a company bill,
 * so a dialog they cannot act on would only stand between a customer and
 * their food. Every other role sees it: the bill belongs to the shop rather
 * than to a job title, and a storekeeper staring at a locked screen will go
 * and find the manager, which is the point.
 *
 * Worth knowing: this means a waiter can still take orders on a `stuck`
 * till. The screen is closed to the people who could clear the bill and open
 * to the people who cannot — deliberate, but say it out loud.
 */
const BLIND_ROLES = ['waiter'];

export function seesBilling(role) {
  return BLIND_ROLES.indexOf(String(role || '').toLowerCase()) === -1;
}

/** Whether this person, on this business, should be prompted at all. */
export function shouldPrompt(role, business) {
  return seesBilling(role) && isDue(business);
}

/* ── money ──────────────────────────────────────────────────────────── */

/** The monthly bill. One number on the business; nothing else sets the price. */
export function monthlyFee(business) {
  const fee = Number(business?.fee);
  return Number.isFinite(fee) && fee > 0 ? fee : 0;
}

export function monthsOf(plan) {
  const months = Number(plan?.months);
  return Number.isFinite(months) && months > 0 ? months : 0;
}

/**
 * What this plan costs.
 *
 * `fee × months`, computed here only to SHOW it. The server computes the same
 * figure from its own copy of the business and charges that — the till never
 * sends an amount, which is the right way round and means a tampered client
 * cannot underpay.
 */
export function totalFor(business, plan) {
  return monthlyFee(business) * monthsOf(plan);
}

/* ── the plans ──────────────────────────────────────────────────────── */

/**
 * The plans worth offering, shortest first.
 *
 * `GET /configuration` returns every plan with no filter of its own — retired
 * ones included — so the `isActive` check has to happen here or a shop is
 * offered a plan that should no longer exist.
 */
export function plansFor(plans) {
  return (Array.isArray(plans) ? plans : [])
    .filter((plan) => plan && plan.isActive !== false && monthsOf(plan) > 0)
    .slice()
    .sort((a, b) => monthsOf(a) - monthsOf(b));
}

/** One month, selected for you. The cheapest commitment is the safe default. */
export function defaultPlan(plans) {
  const list = plansFor(plans);
  return list.find((plan) => monthsOf(plan) === 1) || list[0] || null;
}

export function planIdOf(plan) {
  return plan?._id || plan?.id || null;
}

/* ── dates ──────────────────────────────────────────────────────────── */

/**
 * When the next bill lands, as the phone works it out: the first of the month
 * `months` ahead. Shown before paying, as a preview of what is being bought.
 *
 * Built with a plain moment, not `readDate`, because nothing has been stored
 * yet — this is arithmetic on today's date, not a value read back from the
 * server.
 */
export function previewNextBill(plan) {
  const months = monthsOf(plan);
  if (!months) return '';
  return moment().startOf('month').add(months, 'months').format('DD MMM YYYY');
}

/**
 * The real next-bill date, after paying.
 *
 * This one IS a stored value, so it goes through `readDate` like every other
 * stored date in the app — see utils/appDate.js for why plain `moment()` on a
 * stored value prints hours out.
 */
export function formatNextBill(value) {
  if (!value) return '';
  /**
   * The raw value is checked BEFORE `readDate` touches it.
   *
   * `readDate` falls back to "now" for anything it cannot parse — sensible
   * where a missing timestamp should still sort with its neighbours, wrong
   * here, where it would quietly present today as the date of the next bill.
   * An unreadable value shows nothing at all instead.
   */
  if (!moment.utc(value).isValid()) return '';
  return readDate(value).format('MMM DD, YYYY');
}

/* ── the wallet number ──────────────────────────────────────────────── */

/**
 * A typed number, in the one shape the payment gateway wants.
 *
 * People write the same number four ways — `0615753832`, `615753832`,
 * `+252 61 575 3832`, `252615753832` — and the gateway takes exactly one of
 * them. Rather than ask somebody at a till to get that right, the field shows
 * a fixed `+252` and this puts whatever they typed into the same shape.
 *
 * Returns `{ digits, dial, ok }`: the subscriber digits for display, the full
 * number to send, and whether it is long enough to be worth sending.
 */
export function normalisePhone(input) {
  let digits = String(input || '').replace(/\D/g, '');

  // Typed with the country code, or pasted from a contact list.
  if (digits.startsWith('252')) digits = digits.slice(3);
  // Typed the way it is dialled locally.
  if (digits.startsWith('0')) digits = digits.replace(/^0+/, '');

  // Somali mobile numbers are nine digits. The lower bound is deliberately
  // loose rather than exact: refusing to send is the one failure a locked
  // till cannot recover from, so this rejects a slip of the finger and
  // nothing more.
  const ok = digits.length >= 7 && digits.length <= 12;

  return { digits, dial: digits ? `252${digits}` : '', ok };
}

/** `61 575 3832` — grouped for reading back, never for sending. */
export function prettyPhone(digits) {
  const value = String(digits || '');
  if (value.length < 5) return value;
  return `${value.slice(0, 2)} ${value.slice(2, 5)} ${value.slice(5)}`.trim();
}
